import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, Link } from 'react-router-dom'

import { Box, Tooltip, Typography, useMediaQuery, useTheme, Stack } from '@mui/material'
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro'
import { enUS, svSE } from '@mui/x-data-grid-pro/locales'
import * as datefns from 'date-fns'
import { useAtom } from 'jotai'
import {
    issueFiltersAtom,
    issueGridCurrentPageAtom,
    issueSearchTextfieldAtom,
    issueTableDataAtom,
} from '../../atoms/issueAtom'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatPercentage,
} from '../../packages/utils/numberFormatters'
import { IssueStatusDescription } from './IssueStatus'
import { IssueTypeText } from './IssueType'
import OutcomeChart from './OutcomeChart'
import IssueTypeLabels from './IssueTypeLabels'
import { CommitChart } from './CommitChart'
import { isPreviousIssue } from '../utils/categorizeIssues'
import { calculatePriceChange, getIssueSubscribedSize } from '../../pages/issues/utils'
import { ColoredPercentage } from './utils/ColoredPercentage'
import { PopoverSelector } from './PopoverSelector'
import { TableSettingHeader } from './TableSettingHeader'
import { CustomDataGridToolbar } from '../CustomDataGridToolbar'
import { FullHeightBox } from '../UIComponents'

const ISSUE_TYPES_WITH_COMMIT_CHART = ['Rights issue', 'IPO']
const ISSUE_TYPES_WITH_OUTCOME_CHART = ['Rights issue', 'IPO']

const priceChangeSettings = [
    { id: 'postIssueMax', label: 'Max' },
    { id: 'postIssue3m', label: '3 månader' },
    { id: 'postIssue6m', label: '6 månader' },
    { id: 'postIssue12m', label: '12 månader' },
]

const advisorSettings = [
    { id: 'financialAdvisors', label: 'Fin. rådgivare' },
    { id: 'legalAdvisors', label: 'Legala rådgivare' },
    { id: 'issuingAgents', label: 'Emissionsinstitut' },
]

const issuesCols = (onlyFields, tableSettings, onOpenTableSetting, locale, t, isCompact, detailsPath) => {
    const LinkWrapper = ({ children, to }) => (
        <Link
            to={to}
            style={{
                display: 'flex',
                width: '100%',
                height: '100%',
                textDecoration: 'none',
                color: '#000',
                justifyContent: 'inherit',
            }}
        >
            {children}
        </Link>
    )

    const cols = [
        {
            field: 'issueLabel',
            headerName: 'Emission',
            headerAlign: 'left',
            minWidth: isCompact ? undefined : 400,
            flex: isCompact ? 2 : 1,
            align: 'left',
            valueGetter: (_, row) => row.shareName,
            renderCell: (params) => {
                return (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            sx={{
                                alignItems: { xs: 'start', md: 'center' },
                                paddingY: { xs: 2, md: 0 },
                            }}
                        >
                            <Stack sx={{ justifyContent: 'center', minWidth: '90px' }}>
                                <IssueTypeLabels issue={params.row} />
                            </Stack>
                            <Stack>{params.row.shareName}</Stack>
                        </Stack>
                    </LinkWrapper>
                )
            },
        },
        {
            field: 'issueType',
            headerName: 'Typ',
            headerAlign: 'left',
            minWidth: 160,
            maxWidth: 400,
            flex: 1,
            align: 'left',
            valueGetter: (_, row) => t(`entities.issueType.values.${row.type}`), // Display the type
            sortComparator: (v1, v2) => v1.localeCompare(v2), // Custom sorting based on the 'type' property
            renderCell: (params) => (
                <Tooltip title='Visa detaljer om emissionen'>
                    <LinkWrapper to={detailsPath(params.row)}>
                        <IssueTypeText issueType={params.row.type} />
                    </LinkWrapper>
                </Tooltip>
            ),
        },
        {
            field: 'shareName',
            headerName: 'Aktie',
            headerAlign: 'left',
            minWidth: 180,
            maxWidth: 400,
            flex: 1,
            align: 'left',
            renderCell: (params) => (
                <Tooltip title='Visa detaljer om bolaget'>
                    <LinkWrapper to={`/companies/${params.row.companyId}`}>
                        <Link to={`/companies/${params.row.companyId}`}>
                            {params.row.shareName || params.row.shareId}
                        </Link>
                    </LinkWrapper>
                </Tooltip>
            ),
        },
        {
            field: 'tags',
            headerName: 'Taggar',
            headerAlign: 'left',
            flex: 1,
            minWidth: 90,
            sortable: false,
            display: 'flex',
            align: 'left',
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <IssueTypeLabels issue={params.row} />
                </LinkWrapper>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            headerAlign: 'left',
            flex: 1,
            align: 'left',
            valueGetter: (_, row) => {
                // lower number will be displayed first in the grid
                if (row.statusComplete === null) return '9' // sort last for now
                const hideUntil =
                    row.statusHideUntil && new Date(row.statusHideUntil) > new Date() ? row.statusHideUntil : null
                if (hideUntil) {
                    return `2-${formatDateYYYYMMDD(hideUntil)}` // just before the "all done" status
                }
                if (row.statusComplete) {
                    return row.statusStage && row.statusStage >= 5 ? '5' : '4'
                } else {
                    return '1'
                }
            },
            display: 'flex',
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <FullHeightBox>
                        <IssueStatusDescription
                            shortForm
                            hideUntil={params.row.statusHideUntil}
                            stage={params.row.statusStage}
                            isComplete={params.row.statusComplete}
                        />
                    </FullHeightBox>
                </LinkWrapper>
            ),
        },
        {
            field: 'shareSubPrice',
            headerName: 'Teckningskurs',
            headerAlign: 'left',
            flex: 1,
            align: 'left',
            // Note! This derived field is removed -- needs to be calculated below if used
            valueFormatter: (value) => formatMoney(value, { locale, minDecimals: 2, maxDecimals: 4 }),
        },
        {
            field: 'sharePrice',
            headerName: 'Aktiekurs',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            valueFormatter: (value) => formatMoney(value, { locale, minDecimals: 2, maxDecimals: 4 }),
        },
        {
            field: 'subPriceDiscount',
            headerName: 'Rabatt',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            valueFormatter: () => formatMoney(0.3, { locale, minDecimals: 2, maxDecimals: 4 }),
        },
        {
            field: 'preMoneyValuation',
            headerName: 'Pre-money värdering',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            // NOTE! This derived field is removed -- needs to be calculated below if used
            valueFormatter: (value, row) => formatBigMoney(value, { locale, currencySymbol: row.currency }),
        },
        {
            field: 'date',
            headerName: 'Datum',
            headerAlign: 'left',
            description:
                'Datumet visar sista dagen i teckningsperioden för avslutade företrädesemissioner och första handelsdagen för genomförda IPOer, annars hela teckningsperioden. För övriga emissioner är datumet när emissionen först offentliggjordes.',
            align: 'left',
            flex: 1.2,
            minWidth: 120,
            maxWidth: 300,
            width: 250,
            valueGetter: (_, row) => {
                if (row.type === 'IPO') {
                    return isPreviousIssue(row) ? row.ipoTradingDate : row.subEndDate
                }
                return row.type === 'Rights issue' ? row.subEndDate : row.publicationDate
            },
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1)
                const date2 = new Date(v2)
                return date1 - date2
            },
            renderCell: (params) => {
                const isIpoOrRights = ['IPO', 'Rights issue'].includes(params.row.type)

                return (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <FullHeightBox>
                            <Typography>
                                {params.row.type === 'Rights issue' && isPreviousIssue(params.row)
                                    ? formatDateYYYYMMDD(params.row.subEndDate)
                                    : null}
                                {params.row.type === 'IPO' && isPreviousIssue(params.row)
                                    ? formatDateYYYYMMDD(params.row.ipoTradingDate)
                                    : null}
                                {isIpoOrRights &&
                                    !isPreviousIssue(params.row) &&
                                    `${formatDateYYYYMMDD(params.row.subStartDate)} - ${formatDateYYYYMMDD(
                                        params.row.subEndDate,
                                    )}`}
                                {!isIpoOrRights && formatDateYYYYMMDD(params.row.publicationDate)}
                            </Typography>
                        </FullHeightBox>
                    </LinkWrapper>
                )
            },
        },
        {
            field: 'terms',
            headerName: 'Villkor',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 130,
            renderCell: (params) => {
                if (!params.row.rightsRequired || !params.row.unitsReceived) return null
                return (
                    <>
                        {params.row.rightsRequired} rätter ={'>'} {params.row.unitsReceived}{' '}
                        {params.row.isUnit ? 'units' : 'aktier'}
                    </>
                )
            },
        },
        {
            field: 'dilution',
            headerName: 'Utspädning',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            renderCell: (params) => (
                <>
                    {formatPercentage(
                        (100 * params.row.outstandingShares) /
                            (params.row.outstandingShares + params.row.sharesInUnit * params.row.totalOfferedUnits),
                        { locale, decimals: 1 },
                    )}{' '}
                </>
            ),
        },
        {
            field: 'offeredSize',
            headerName: 'Belopp',
            headerAlign: 'left',
            align: 'left',
            minWidth: 70,
            flex: 1,
            valueGetter: (value, issue) => {
                return issue.totalUnitSize
            },
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <FullHeightBox>
                        {params.value != null
                            ? formatBigMoney(params.value, { locale, currencySymbol: params.row.currency })
                            : null}
                    </FullHeightBox>
                </LinkWrapper>
            ),
        },
        {
            field: 'subscribedSize',
            headerName: 'Tecknat belopp',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            valueGetter: (value, issue) => {
                return getIssueSubscribedSize(issue)
            },
            renderCell: (params) => (
                <LinkWrapper to={detailsPath(params.row)}>
                    <FullHeightBox>
                        {params.value != null
                            ? formatBigMoney(params.value, { locale, currencySymbol: params.row.currency })
                            : null}
                    </FullHeightBox>
                </LinkWrapper>
            ),
        },
        {
            field: 'financialAdvisors',
            headerAlign: 'left',
            align: 'left',
            minWidth: 120,
            flex: 1,
            sortable: false,
            renderHeader: () => {
                const header = advisorSettings.find(({ id }) => id === tableSettings.advisors.value[0])?.label

                return <TableSettingHeader header={header} onClick={onOpenTableSetting} setting='advisors' />
            },
            renderCell: (params) => {
                const advisorKey = tableSettings.advisors.value[0]
                const advisors = params.row[advisorKey]
                const count = advisors?.length

                if (!count) {
                    return <LinkWrapper to={detailsPath(params.row)} />
                }

                const suffix = count > 1 ? ` +${count - 1}` : ''
                return (
                    <LinkWrapper to={detailsPath(params.row)}>
                        {advisors[0].name} {suffix}
                    </LinkWrapper>
                )
            },
        },
        {
            field: 'totalCommitment',
            headerName: 'Säkerställandegrad',
            headerAlign: 'center',
            align: 'center',
            minWidth: 200,
            flex: 1,
            cellClassName: 'align-content--center',
            renderCell: (params) =>
                ISSUE_TYPES_WITH_COMMIT_CHART.includes(params.row.type) ? (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <FullHeightBox>
                            <CommitChart issue={params.row} size='small' />
                        </FullHeightBox>
                    </LinkWrapper>
                ) : null,
        },
        {
            field: 'outcome',
            headerName: 'Utfall',
            headerAlign: 'center',
            align: 'center',
            minWidth: 200,
            flex: 1,
            sortable: false,
            cellClassName: 'align-content--center',
            renderCell: (params) =>
                ISSUE_TYPES_WITH_OUTCOME_CHART.includes(params.row.type) ? (
                    <LinkWrapper to={detailsPath(params.row)}>
                        <FullHeightBox>
                            <OutcomeChart issue={params.row} />
                        </FullHeightBox>
                    </LinkWrapper>
                ) : (
                    <LinkWrapper to={detailsPath(params.row)} />
                ),
        },
        {
            field: 'priceChange',
            headerAlign: 'center',
            align: 'center',
            minWidth: 170,
            flex: 1,
            renderHeader: () => (
                <TableSettingHeader header='Kursutveckling' onClick={onOpenTableSetting} setting='priceChange' />
            ),
            sortable: true,
            valueGetter: (params, issue) => {
                const priceChangeFilterValue = tableSettings.priceChange?.value[0]
                const tradeStats = issue.tradesStats.find(({ periodType }) => periodType === priceChangeFilterValue)

                const priceChange = calculatePriceChange(tradeStats, issue.subscriptionRate)
                return priceChange
            },
            renderCell: ({ formattedValue, ...params }) => {
                if (formattedValue === null) return <LinkWrapper to={detailsPath(params.row)}>'-'</LinkWrapper>

                return (
                    <Tooltip title='Kursutvecklingen avser förändringen från teckningskursen till dagens aktiekurs, eller aktiekursen vid vald tidsperiod.'>
                        <LinkWrapper to={detailsPath(params.row)}>
                            <div>
                                <ColoredPercentage value={formattedValue} />
                            </div>
                        </LinkWrapper>
                    </Tooltip>
                )
            },
        },
        {
            field: 'updatedAt',
            headerName: 'Uppdaterad',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            valueFormatter: (value) => datefns.format(new Date(value), 'yyyy-MM-dd HH:mm:ss'),
        },
    ]
    if (t) {
        cols.forEach((col) => {
            if (!col.headerName && col.headerNameI18n) {
                col.headerName = t(col.headerNameI18n)
            }
        })
    }
    return onlyFields ? cols.filter((col) => onlyFields.includes(col.field)) : cols
}

const gridTextI18n = (locale) => {
    if (locale?.startsWith('sv')) {
        return svSE.components.MuiDataGrid.defaultProps.localeText
    } else {
        return enUS.components.MuiDataGrid.defaultProps.localeText
    }
}

function CustomNoResultsOverlay() {
    return (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1'>Inga emissioner hittades</Typography>
        </Box>
    )
}

function IssuesGrid({
    isCompact = false,
    isLoading,
    issues,
    sortModel = null,
    pagination = true,
    pageSizeInitialState = 10,
    pageSizeOptions = [10, 25, 50, 100],
    hideFooter = false,
    fields = null,
    hasSearchFilter = false,
    detailsPath = null,
}) {
    const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    const [issueGridCurrentPage, setIssueGridCurrentPage] = useAtom(issueGridCurrentPageAtom)
    const [searchTextfield, setSearchTextfield] = useAtom(issueSearchTextfieldAtom)
    const [filters] = useAtom(issueFiltersAtom)
    const [tableSettings, setTableSettings] = useAtom(issueTableDataAtom)
    const [tableSettingInEdit, setTableSettingInEdit] = useState(null)
    const [pageSize, setPageSize] = useState(pageSizeInitialState)

    // Initialize filterModel with quickFilterValues and an empty items array
    const [filterModel, setFilterModel] = useState({
        items: [], // Ensure items is an empty array initially
        quickFilterValues: [hasSearchFilter ? searchTextfield : ''], // Default quick filter value
    })

    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    useEffect(() => {
        setIssueGridCurrentPage(0)
    }, [setIssueGridCurrentPage, filters])

    const navigate = useNavigate()

    const handleFilterModelChange = (newFilterModel) => {
        setFilterModel({
            ...newFilterModel,
            items: newFilterModel.items || [], // Ensure items is always an array
        })
        setIssueGridCurrentPage(0)
        setSearchTextfield(newFilterModel.quickFilterValues[0])
    }
    const slots = hasSearchFilter
        ? {
              slots: {
                  toolbar: CustomDataGridToolbar,
                  noResultsOverlay: CustomNoResultsOverlay,
                  noRowsOverlay: CustomNoResultsOverlay,
              },
          }
        : { slots: { noResultsOverlay: CustomNoResultsOverlay, noRowsOverlay: CustomNoResultsOverlay } }

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    const handleTableSettingChange = useCallback(
        async (settingName, selectedItems) => {
            setTableSettings((prevFilters) => ({
                ...prevFilters,
                [settingName]: { value: selectedItems },
            }))
        },
        [setTableSettings],
    )

    const onOpenTableSetting = useCallback((element, setting) => {
        setPopoverAnchorEl(element)
        setTableSettingInEdit(setting)
    })

    detailsPath = detailsPath || ((issue) => `/issues/${issue.id}`)

    const grid = (
        // adapt filter: https://mui.com/x/react-data-grid/filtering/customization/#create-a-custom-operator
        <>
            {popoverAnchorEl && tableSettingInEdit === 'priceChange' && (
                <PopoverSelector
                    anchorEl={popoverAnchorEl}
                    label='Välj tidsperiod sedan emisson'
                    onClose={() => setPopoverAnchorEl(null)}
                    onChange={(selectedItems) => void handleTableSettingChange('priceChange', selectedItems)}
                    options={priceChangeSettings}
                    selectedId={tableSettings.priceChange.value}
                />
            )}
            {popoverAnchorEl && tableSettingInEdit === 'advisors' && (
                <PopoverSelector
                    anchorEl={popoverAnchorEl}
                    label='Välj typ av rådgivare att visa'
                    onClose={() => setPopoverAnchorEl(null)}
                    onChange={(selectedItems) => void handleTableSettingChange('advisors', selectedItems)}
                    options={advisorSettings}
                    selectedId={tableSettings.advisors.value}
                />
            )}
            <DataGridPro
                sx={{
                    ...disableSelectedSellOutlineStyle,
                    '& .MuiDataGrid-main': { width: 0, minWidth: '100%' }, // https://github.com/mui/mui-x/issues/8168#issuecomment-1465784733
                    width: '100%',
                    '& .even': {
                        backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                    },
                    '& .odd': {
                        // backgroundColor: theme.palette.rowColor.light, // Use palette color for odd rows
                    },
                    '& .MuiDataGrid-row': {
                        cursor: 'pointer',
                    },
                    '.align-content--center': {
                        alignContent: 'center',
                    },
                }}
                sortingOrder={['asc', 'desc']}
                loading={isLoading}
                localeText={gridTextI18n(locale)}
                pagination={pagination}
                initialState={{
                    sorting: { sortModel },
                }}
                pageSizeOptions={pageSizeOptions}
                rowsPerPageOptions={pageSizeOptions}
                getRowHeight={() => (isSmallScreen ? 'auto' : null)}
                disableColumnMenu
                hideFooter={hideFooter}
                disableRowSelectionOnClick
                rows={issues}
                columns={issuesCols(fields, tableSettings, onOpenTableSetting, locale, t, isCompact, detailsPath)}
                density={isCompact ? 'compact' : 'standard'}
                {...slots}
                slotProps={{
                    loadingOverlay: {
                        variant: 'skeleton',
                        noRowsVariant: 'skeleton',
                    },
                }}
                showToolbar={hasSearchFilter}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                paginationModel={{ pageSize, page: issueGridCurrentPage }}
                onPaginationModelChange={({ page, pageSize }) => {
                    setIssueGridCurrentPage(page)
                    setPageSize(pageSize)
                }}
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange} // Capture changes to the filter model
            />
        </>
    )
    return grid
}

export default IssuesGrid
